/**
 * VerooglMetricsBar — mirrors the Metric Savings Bar on the main
 * `/demo` page, but every tile is reactive to the operator's Phase 1
 * activity (leads scraped, hot leads, pipeline value, est. annual
 * savings).  Visually identical to the Nexengines Platform Demo:
 * glass-card chrome, ambient blur dots, gradient-emerald headline.
 */
import React, { useMemo } from "react";
import { BadgeDollarSign, BoxSelect, Zap, Flame, BadgeCheck } from "lucide-react";

const TINT = {
  emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  cyan:    "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  rose:    "text-rose-400 bg-rose-500/10 border-rose-500/30",
  purple:  "text-purple-400 bg-purple-500/10 border-purple-500/30",
};

const fmt = (n) => "$" + Math.round(n).toLocaleString();

const Kpi = ({ Icon, tint, label, sub, children, divider }) => (
  <div className={`flex items-center gap-4 ${divider ? "lg:border-r lg:border-white/10 lg:pr-6" : ""}`}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${TINT[tint]} shrink-0`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">{label}</div>
      <div className="font-mono text-xl font-bold mt-0.5">{children}</div>
      <div className="font-mono text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  </div>
);

const PER_PIPELINE_MERCHANT_BUMP = 1250;
const PER_APPROVED_MERCHANT_BUMP = 2500;

const savingsSub = (nex) => {
  const b = nex?.breakdown;
  if (nex?.override) return "Nexengines estimate · custom figure";
  if (!b) return "Link a friction audit to calculate";
  const tools = b.tools_replaced.length;
  return `${b.hours_per_year.toLocaleString()} hrs/yr saved · ${tools} tool${tools === 1 ? "" : "s"} replaced`;
};

export default function VerooglMetricsBar({ scoringPool, pipeline, hotCount, nexSavings, approvedCount = 0, riskBonus = 0 }) {
  const scraped = scoringPool.length;
  const pipelineValue = useMemo(
    () => pipeline.reduce((a, b) => a + (b.volume || 0), 0),
    [pipeline]
  );
  const estAnnualSavings = (nexSavings?.annual || 0) + pipeline.length * PER_PIPELINE_MERCHANT_BUMP + approvedCount * PER_APPROVED_MERCHANT_BUMP + riskBonus;

  return (
    <div
      data-testid="veroogle-metrics-bar"
      className="glass-card rounded-2xl p-5 border border-slate-800 relative overflow-hidden"
    >
      <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-blue/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-brand-emerald/10 rounded-full blur-3xl pointer-events-none" />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
        <Kpi Icon={BadgeDollarSign} tint="emerald" label="Nexengines · Est. Annual Savings" sub={<span data-testid="veroogle-metric-savings-breakdown">{savingsSub(nexSavings)}</span>} divider>
          <span className="gradient-emerald" data-testid="veroogle-metric-savings">
            {fmt(estAnnualSavings)} / yr
          </span>
        </Kpi>
        <Kpi Icon={BoxSelect} tint="cyan" label="Leads Scraped" sub="Google Places + local permits" divider>
          <span className="text-slate-100" data-testid="veroogle-metric-scraped">
            {scraped} merchants
          </span>
        </Kpi>
        <Kpi Icon={Flame} tint="rose" label="Hot Leads" sub="Score ≥ 70 · ready to call" divider>
          <span className="text-slate-100" data-testid="veroogle-metric-hot">
            {hotCount} hot
          </span>
        </Kpi>
        <Kpi Icon={Zap} tint="purple" label="Pipeline Volume" sub={`${pipeline.length} merchants in motion`} divider>
          <span className="text-slate-100" data-testid="veroogle-metric-pipeline">
            {fmt(pipelineValue)} / mo
          </span>
        </Kpi>
        <Kpi Icon={BadgeCheck} tint="emerald" label="Merchants Onboarded" sub="Verified & underwritten">
          <span className="text-slate-100" data-testid="veroogle-metric-onboarded">
            {approvedCount} live
          </span>
        </Kpi>
      </div>
    </div>
  );
}
